import { useCallback } from 'react';

import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { createSkillDetailTilePayload, SKILL_DETAIL_TILE_SLOT_KEY } from '../../skills';
import { useUrlTiles } from '../../url-tiles';
import { useActiveJobArchitectureId } from '../url-navigation/job-architecture-scope';
import { useUrlSelection } from '../url-navigation/use-url-selection';
import {
    createJsaArchiveTilePayload,
    createJsaChangelogTilePayload,
    createJsaClusterDetailTilePayload,
    createJsaCompareTilePayload,
    createJsaFamilyDetailTilePayload,
    createJsaProfileDetailTilePayload,
    isJsaCompareTilePayload,
    isJsaProfileDetailTilePayload,
    JSA_ARCHIVE_TILE_SLOT_KEY,
    JSA_CHANGELOG_TILE_SLOT_KEY,
    JSA_COMPARE_TILE_SLOT_KEY,
    JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
} from '../url-tile-payloads';

type OpenJobProfileTileParams = {
    readonly isArchived?: boolean;
    readonly mode: 'edit' | 'view';
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profileId: string;
    readonly type: 'job-level' | 'job-role';
    readonly version?: number;
};

type OpenCompareTileParams = {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profileId: string;
    readonly type: 'job-level' | 'job-role';
    readonly versionA: string;
    readonly versionB: string;
};

export const useJobArchitectureUrlTileActions = () => {
    const architectureId = useActiveJobArchitectureId();
    const { setSelectedClusterId, setSelectedFamilyId } = useUrlSelection();
    const { closeTile, tiles, updateTile } = useUrlTiles();

    const hasTileInSlot = useCallback(
        (slotKey: string): boolean => tiles.some((tile) => tile.slotKey === slotKey),
        [tiles],
    );

    const hasPrimaryDetailTile = useCallback(
        (): boolean => hasTileInSlot(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY),
        [hasTileInSlot],
    );

    const hasCompareTile = useCallback((): boolean => hasTileInSlot(JSA_COMPARE_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasChangelogTile = useCallback((): boolean => hasTileInSlot(JSA_CHANGELOG_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasArchiveTile = useCallback((): boolean => hasTileInSlot(JSA_ARCHIVE_TILE_SLOT_KEY), [hasTileInSlot]);

    const hasSkillTile = useCallback((): boolean => hasTileInSlot(SKILL_DETAIL_TILE_SLOT_KEY), [hasTileInSlot]);

    const openFamilyTile = useCallback(
        (familyId: string, mode: 'edit' | 'view'): void => {
            setSelectedFamilyId(familyId);
            updateTile(createJsaFamilyDetailTilePayload({ architectureId, familyId, mode }));
        },
        [architectureId, setSelectedFamilyId, updateTile],
    );

    const openClusterTile = useCallback(
        (clusterId: string, familyId: string, mode: 'edit' | 'view'): void => {
            setSelectedFamilyId(familyId);
            setSelectedClusterId(clusterId);
            updateTile(createJsaClusterDetailTilePayload({ architectureId, clusterId, familyId, mode }));
        },
        [architectureId, setSelectedClusterId, setSelectedFamilyId, updateTile],
    );

    const openJobProfileTile = useCallback(
        ({ isArchived, mode, preferredLanguage, profileId, type, version }: OpenJobProfileTileParams): void => {
            closeTile(SKILL_DETAIL_TILE_SLOT_KEY);
            updateTile(
                createJsaProfileDetailTilePayload({
                    architectureId,
                    isArchived,
                    itemType: type,
                    mode,
                    preferredLanguage,
                    profileId,
                    version,
                }),
            );
        },
        [architectureId, closeTile, updateTile],
    );

    const openCompareTile = useCallback(
        ({ preferredLanguage, profileId, type, versionA, versionB }: OpenCompareTileParams): void => {
            updateTile(
                createJsaCompareTilePayload({
                    architectureId,
                    itemType: type,
                    preferredLanguage,
                    profileId,
                    versionA,
                    versionB,
                }),
            );
        },
        [architectureId, updateTile],
    );

    const openChangelogTile = useCallback((): void => {
        updateTile(createJsaChangelogTilePayload({ architectureId }));
    }, [architectureId, updateTile]);

    const openArchiveTile = useCallback((): void => {
        updateTile(createJsaArchiveTilePayload({ architectureId }));
    }, [architectureId, updateTile]);

    const openSkillTile = useCallback(
        (skillId: string, preferredLanguage?: LanguageCodeForJSA, proficiency?: SkillProficiencyLevel | null): void => {
            updateTile(
                createSkillDetailTilePayload({ preferredLanguage, skillId, proficiency: proficiency ?? undefined }),
            );
        },
        [updateTile],
    );

    const closePrimaryDetailTile = useCallback((): void => {
        closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeCompareTile = useCallback((): void => {
        closeTile(JSA_COMPARE_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeChangelogTile = useCallback((): void => {
        closeTile(JSA_CHANGELOG_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeArchiveTile = useCallback((): void => {
        closeTile(JSA_ARCHIVE_TILE_SLOT_KEY);
    }, [closeTile]);

    const closeSkillTile = useCallback((): void => {
        closeTile(SKILL_DETAIL_TILE_SLOT_KEY);
    }, [closeTile]);

    const updateJobProfileTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

            if (isJsaProfileDetailTilePayload(primaryPayload)) {
                updateTile({ ...primaryPayload, preferredLanguage });
            }
        },
        [tiles, updateTile],
    );

    const updateCompareTileLanguage = useCallback(
        (preferredLanguage: LanguageCodeForJSA): void => {
            const comparePayload = tiles.find((tile) => tile.slotKey === JSA_COMPARE_TILE_SLOT_KEY)?.payload;

            if (isJsaCompareTilePayload(comparePayload)) {
                updateTile({ ...comparePayload, preferredLanguage });
            }
        },
        [tiles, updateTile],
    );

    const updateJobProfileTileVersion = useCallback(
        (version: number): void => {
            const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;

            if (isJsaProfileDetailTilePayload(primaryPayload)) {
                updateTile({ ...primaryPayload, version });
            }
        },
        [tiles, updateTile],
    );

    return {
        closeArchiveTile,
        closeChangelogTile,
        closeCompareTile,
        closePrimaryDetailTile,
        closeSkillTile,
        hasArchiveTile,
        hasChangelogTile,
        hasCompareTile,
        hasPrimaryDetailTile,
        hasSkillTile,
        openArchiveTile,
        openChangelogTile,
        openClusterTile,
        openCompareTile,
        openFamilyTile,
        openJobProfileTile,
        openSkillTile,
        updateCompareTileLanguage,
        updateJobProfileTileLanguage,
        updateJobProfileTileVersion,
    };
};
